import { Component, OnInit, ChangeDetectionStrategy, ElementRef } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CoreService } from '../../services'
import { OAuthSession } from 'depoto-core/src/models'
import { NotificationService } from '../../services'
import { Events } from 'depoto-core/src/utils/EventsUtil'
import { TranslateService } from '@ngx-translate/core'
import { AVAILABLE_LANGUAGES, getSupportedLanguage } from '../../configs/languages'
import { injectQueryClient } from '@tanstack/angular-query-experimental'
import { MultiSessionService, SessionEntry, SwitchSessionResult } from '../../services/multi-session.service'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit {
  private queryClient = injectQueryClient()
  client_type_options = [
    {
      id: 'prod',
      name: 'production',
    },
    {
      id: 'stage',
      name: 'testing',
    },
    {
      id: 'custom',
      name: 'custom',
    },
  ]
  loginForm = new FormGroup({
    email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    password: new FormControl({ value: '', disabled: false }, [Validators.required]),
    clientType: new FormControl(
      {
        value: window.location.href.includes('local') || window.location.href.includes('stage') ? 'stage' : 'prod',
        disabled: false,
      },
      [Validators.required]
    ),
    customUrl: new FormControl({
      value: 'http://localhost/tomatom/depoto-server/web/app_dev.php/',
      disabled: false,
    }),
    // rememberMe: new FormControl({ value: true, disabled: false }),
    rememberMe: new FormControl({ value: true, disabled: false }, []),
  })
  loading = false
  isShowingAdvanced = false
  showPassword = false
  submitted = false
  year = `${new Date().getFullYear()}`

  // Accounts remembered from previous logins (see MultiSessionService), offered here so logging
  // out of one account doesn't force the user to retype credentials for the others they still
  // have stored.
  rememberedSessions: SessionEntry[] = []
  switchingSessionId: string | null = null
  // Set when a remembered session's token turned out to be dead — locks email/server so the
  // form can only refresh that same session's token, and shows a hint instead of the generic form.
  reauthSessionId: string | null = null

  public readonly availableLanguages = AVAILABLE_LANGUAGES
  public selectedLanguage: string

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private multiSession: MultiSessionService,
    private host: ElementRef<HTMLElement>
  ) {
    this.selectedLanguage = getSupportedLanguage(this.translateService.getBrowserLang())
    this.translateService.use(this.selectedLanguage)
  }

  ngOnInit() {
    this.reloadTokens()
    this.refreshRememberedSessions()
    this.openPendingSessionIfAny()
  }

  private refreshRememberedSessions(): void {
    this.rememberedSessions = this.multiSession.getSessionsList()
  }

  // A tab opened from a session-item link (see box-user) has MultiSessionService already
  // verifying the target session (kicked off from its own constructor - see there for why it
  // can't just hand us an id to switch to ourselves: this component can be destroyed and
  // recreated before that verification settles). Await the shared outcome and apply it exactly
  // like a same-tab click would, so an expired target lands on the normal locked/pre-filled
  // reauth prompt instead of the plain form.
  private openPendingSessionIfAny(): void {
    const pending = this.multiSession.getPendingOpenSessionOutcome()
    if (!pending) return
    pending.then(({ entry, result }) => this.applySwitchOutcome(entry, result))
  }

  async switchToRemembered(entry: SessionEntry): Promise<void> {
    if (this.switchingSessionId) return
    this.switchingSessionId = entry.id
    const result = await this.multiSession.switchToSession(entry.id)
    this.switchingSessionId = null
    this.applySwitchOutcome(entry, result)
  }

  private applySwitchOutcome(entry: SessionEntry, result: SwitchSessionResult): void {
    if (result.ok) {
      if (!this.core.services.vat.defaultVat) {
        this.core.services.vat.getDefaultVat()
      }
      this.queryClient.invalidateQueries()
      return
    }
    if (result.reason === 'expired') {
      // An expired saved session is a heads-up, not a failure: a plain info toast
      // (same styling as the app's other notifications), plus the inline re-auth
      // hint below (reauthSessionId) that persists while the user logs back in.
      this.notificationService.info(this.translateService.instant('user.session-expired'))
      this.reauthSessionId = entry.id
      this.loginForm.patchValue({
        email: entry.email,
        password: '',
        clientType: entry.clientType,
        customUrl: entry.clientCustomUri ?? this.loginForm.value.customUrl,
      })
    } else {
      // Not a confirmed expiry (e.g. network error) - the account's token may still be fine, so
      // don't force it into the re-auth flow, just let the user retry the switch.
      this.notificationService.error(this.translateService.instant('user.session-switch-failed'))
    }
  }

  cancelReauth(): void {
    this.reauthSessionId = null
    this.loginForm.patchValue({ email: '', password: '' })
  }

  removeRememberedSession(id: string, event: Event): void {
    event.stopPropagation()
    this.multiSession.removeSession(id)
    this.refreshRememberedSessions()
    if (this.reauthSessionId === id) this.cancelReauth()
  }

  async reloadTokens(): Promise<any> {
    this.loading = true
    if (this.core.services.oauth.isAuthenticated()) {
      if (
        !(
          (!this.core.services.oauth.session.isRememberMe && (await this.core.services.oauth.getAccessToken())) ||
          (this.core.services.oauth.session.isRememberMe && (await this.core.services.oauth.getRefreshToken()))
        )
      ) {
        return new Promise<void>(r => r())
      }
      if (!this.core.services.vat.defaultVat) {
        this.core.services.vat.getDefaultVat()
      }
      setTimeout(() => {
        this.loading = false
        Events.dispatch('init:loading')
      }, 200)
    }
    setTimeout(() => (this.loading = false), 250)
  }

  async login() {
    this.submitted = true
    this.reconcileAutofilledCredentials()
    if (this.loginForm.invalid) {
      this.notificationService.error(this.translateService.instant('login.error.msg'))
      return
    }
    const session = new OAuthSession({
      email: this.loginForm.value.email,
      password: this.loginForm.value.password,
      clientType: this.loginForm.value.clientType,
      isRememberMe: this.loginForm.value.rememberMe,
      clientCustomUri: this.loginForm.value.customUrl,
    })
    this.loading = true
    this.core.services.oauth
      .getToken(session)
      .then(async res => {
        if (res) {
          this.multiSession.saveCurrentSession()
          await this.core.services.user.setCurrentUser(session.email)
          // todo wtf this.core.cleanLocalStorage(session.email, 'login')
          if (!this.core.services.vat.defaultVat) {
            this.core.services.vat.getDefaultVat()
          }
          this.queryClient.invalidateQueries()
          this.reauthSessionId = null
        } else {
          throw new Error()
        }
      })
      .catch(err => {
        this.notificationService.error(this.translateService.instant('login.error'))
      })
      .finally(() => (this.loading = false))
  }

  // Browser / password-manager autofill can populate the rendered inputs without firing the
  // events form-row-input relays to the reactive control (most reliably reproduced right after
  // "use a different account" swaps the input elements, which triggers a silent re-autofill).
  // Pull the DOM values back into the form before validating so an autofilled login still submits.
  private reconcileAutofilledCredentials(): void {
    const readInput = (dataCy: string): string =>
      (this.host.nativeElement.querySelector(`input[data-cy="${dataCy}"]`) as HTMLInputElement | null)?.value ?? ''
    const patch: { email?: string; password?: string } = {}
    const email = readInput('username')
    const password = readInput('password')
    if (email && email !== this.loginForm.get('email')?.value) patch.email = email
    if (password && password !== this.loginForm.get('password')?.value) patch.password = password
    if (patch.email !== undefined || patch.password !== undefined) {
      this.loginForm.patchValue(patch)
    }
  }

  setCustomServerUrl(): void {
    this.core.services.storage.set(this.core.services.storage.keys.auth.customServerUrl, this.loginForm.value.customUrl)
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.loginForm.controls
  }

  setLanguage(event: any) {
    this.selectedLanguage = getSupportedLanguage(event)
    this.translateService.use(this.selectedLanguage)
  }
}
